// The PURE FLIP arithmetic for the swap transition (ISLAND PR2 — "kill the
// blink", arch must-nail #2). FLIP = First, Last, Invert, Play: to move an item
// from an old position to a new one WITHOUT a layout-thrashing per-frame
// animation, you record where it was (First), let the DOM lay it out where it
// will be (Last), compute the delta, apply the INVERTED delta as a transform so
// it visually starts back at First, then spring the transform to zero (Play) —
// the compositor does the move, one cheap transform, and the item reads as
// MOVING to its new place rather than BLINKING there (canon 75 §1 rule 2: "items
// move, they do not blink").
//
// This module is the load-bearing decision of the whole transition, and it is a
// PURE function of plain rectangles — no DOM, no retained state, no motion lib —
// so it is unit-tested under Node's built-in runner exactly like the stroke
// classifier (recognizer.ts) and the overlay geometry (overlay-geometry.ts):
//   node --experimental-strip-types --test flip.test.ts
//
// The DOM wiring in swap.ts (reading each keyed element's box before the swap,
// matching old→new by key, writing the inverted transform and springing it to
// zero through motion.ts) is exercised by the Playwright pointer/driver check
// against the swap demo harness — the same division overlay-geometry.ts keeps
// with overlay.ts (canon 91 §1: no test framework, no runtime dependency).

/** A box in the coordinate space the caller measured in (viewport px from
 *  getBoundingClientRect, or field-space — the math is translation-invariant so
 *  either works as long as First and Last are measured the same way). */
export interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The inverted transform that makes an element at its LAST (new) box visually
 *  start at its FIRST (old) box. `dx`/`dy` translate it back; `sx`/`sy` scale it
 *  back to the old size. Playing the FLIP springs all four to the identity
 *  (0,0,1,1), so the element travels and grows/shrinks into its new place. */
export interface Invert {
  dx: number;
  dy: number;
  sx: number;
  sy: number;
}

/** The identity inversion — no travel, no scale. An item that did not move
 *  resolves to this, so Play is a no-op and nothing animates (a quiet item in a
 *  reorder is left alone, not springing against itself). */
export const IDENTITY: Invert = { dx: 0, dy: 0, sx: 1, sy: 1 };

/**
 * Compute the INVERT step: given where an item WAS (first) and where it now IS
 * (last), the transform that visually places the last box back onto the first.
 *
 *   dx = first.x - last.x   (how far LEFT of its new spot it used to be)
 *   dy = first.y - last.y
 *   sx = first.width  / last.width    (how much narrower/wider it used to be)
 *   sy = first.height / last.height
 *
 * A zero/near-zero last dimension (a collapsed element) falls back to scale 1,
 * so a degenerate box never yields a divide-by-zero or an Infinity transform.
 */
export function invert(first: Box, last: Box): Invert {
  return {
    dx: first.x - last.x,
    dy: first.y - last.y,
    sx: last.width > 0.001 ? first.width / last.width : 1,
    sy: last.height > 0.001 ? first.height / last.height : 1,
  };
}

/** True when an inversion is within `eps` of the identity on every axis — the
 *  item effectively did not move or resize, so Play would animate nothing and is
 *  skipped. The translate epsilon is px; the scale epsilon is a ratio. */
export function isNegligible(inv: Invert, epsPx = 0.5, epsScale = 0.002): boolean {
  return (
    Math.abs(inv.dx) <= epsPx &&
    Math.abs(inv.dy) <= epsPx &&
    Math.abs(inv.sx - 1) <= epsScale &&
    Math.abs(inv.sy - 1) <= epsScale
  );
}

/** The CSS transform string for an inversion, in the exact order a FLIP needs:
 *  translate FIRST, then scale, so the scale does not move the translation's
 *  origin. The transform-origin must be the element's top-left (`0 0`) for this
 *  to land true — swap.ts sets that on the elements it FLIPs. Rounded so the
 *  written string is stable (and a probe can parse it) without sub-px churn. */
export function invertTransform(inv: Invert): string {
  const dx = round2(inv.dx);
  const dy = round2(inv.dy);
  const sx = round3(inv.sx);
  const sy = round3(inv.sy);
  return `translate(${dx}px, ${dy}px) scale(${sx}, ${sy})`;
}

function round2(f: number): number {
  return Math.round(f * 100) / 100;
}
function round3(f: number): number {
  return Math.round(f * 1000) / 1000;
}

/**
 * Match keyed FIRST boxes to keyed LAST boxes and classify every key into the
 * three FLIP groups a swap produces:
 *   - `moved`  — present before AND after: FLIP it from its old box to its new
 *                box (the reorder case; the identity-movers are filtered out so
 *                a still item is never animated).
 *   - `entered`— present only after: a fragment/pane/item that APPEARED, to be
 *                entered (fade + rise in).
 *   - `exited` — present only before: one that LEFT, to be exited (fade + fall
 *                out) from its last-known box.
 *
 * Pure: it takes two maps of key→box and returns the plan. swap.ts measures the
 * boxes from the DOM and plays the plan through motion.ts; the decision of WHAT
 * moves / enters / leaves — the part that must be right — is here and tested.
 */
export interface FlipPlan {
  moved: Array<{ key: string; first: Box; last: Box; invert: Invert }>;
  entered: string[];
  exited: Array<{ key: string; box: Box }>;
}

export function planFlip(
  first: Map<string, Box>,
  last: Map<string, Box>,
  opts: { epsPx?: number; epsScale?: number } = {},
): FlipPlan {
  const moved: FlipPlan["moved"] = [];
  const entered: string[] = [];
  const exited: FlipPlan["exited"] = [];

  for (const [key, lastBox] of last) {
    const firstBox = first.get(key);
    if (!firstBox) {
      entered.push(key);
      continue;
    }
    const inv = invert(firstBox, lastBox);
    if (!isNegligible(inv, opts.epsPx, opts.epsScale)) {
      moved.push({ key, first: firstBox, last: lastBox, invert: inv });
    }
  }
  for (const [key, firstBox] of first) {
    if (!last.has(key)) exited.push({ key, box: firstBox });
  }

  return { moved, entered, exited };
}
